import { useState } from 'react';
import { Paper, Table, Switch, Text, Button, Group, Alert, Stack } from '@mantine/core';
import { IconInfoCircle, IconDeviceFloppy } from '@tabler/icons-react';
import { useOrganization } from '@/hooks/useOrganization';
import {
  useOrganizationSettings,
  useUpdateOrganizationSettings,
  type AssetFieldSettings as AssetFieldSettingsType,
  type AssetFieldConfig,
} from '@/hooks/api/useOrganizationSettings';

// Default fields - only Tag ID and Description are required initially
const defaultFields: Record<keyof AssetFieldSettingsType, AssetFieldConfig> = {
  tag_id: { visible: true, required: true, label: 'Tag ID' },
  description: { visible: true, required: true, label: 'Description' },
  brand: { visible: true, required: false, label: 'Brand' },
  model: { visible: true, required: false, label: 'Model' },
  serial_number: { visible: true, required: false, label: 'Serial Number' },
  status: { visible: true, required: false, label: 'Status' },
  purchase_date: { visible: true, required: false, label: 'Purchase Date' },
  purchased_from: { visible: true, required: false, label: 'Purchased From' },
  cost: { visible: true, required: false, label: 'Cost' },
  warranty_period: { visible: true, required: false, label: 'Warranty Period (months)' },
  category_id: { visible: true, required: false, label: 'Category' },
  location_id: { visible: true, required: false, label: 'Location' },
  department_id: { visible: true, required: false, label: 'Department' },
  photo_url: { visible: true, required: false, label: 'Photo' },
};

export function AssetFieldSettings() {
  const { organizationId } = useOrganization();
  const { data: settings, isLoading } = useOrganizationSettings(organizationId);
  const updateSettings = useUpdateOrganizationSettings();

  const currentFields = { ...defaultFields, ...(settings?.assetFields || {}) };
  const [fieldSettings, setFieldSettings] = useState<AssetFieldSettingsType>(currentFields);
  const [hasChanges, setHasChanges] = useState(false);

  const handleVisibilityChange = (fieldName: keyof AssetFieldSettingsType, visible: boolean) => {
    setFieldSettings((prev) => ({
      ...prev,
      [fieldName]: {
        ...prev[fieldName],
        visible,
        // If hiding a field, it can't be required
        required: visible ? prev[fieldName].required : false,
      },
    }));
    setHasChanges(true);
  };

  const handleRequiredChange = (fieldName: keyof AssetFieldSettingsType, required: boolean) => {
    setFieldSettings((prev) => ({
      ...prev,
      [fieldName]: {
        ...prev[fieldName],
        required,
        // If making a field required, it must be visible
        visible: required ? true : prev[fieldName].visible,
      },
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (!organizationId) return;

    await updateSettings.mutateAsync({
      organizationId,
      settings: {
        ...settings,
        assetFields: fieldSettings,
      },
    });

    setHasChanges(false);
  };

  const handleReset = () => {
    setFieldSettings(currentFields);
    setHasChanges(false);
  };

  if (isLoading) {
    return <Text>Loading settings...</Text>;
  }

  return (
    <Stack gap="md">
      <Alert icon={<IconInfoCircle size={16} />} color="blue">
        <Text size="sm">
          Configure which fields are shown and required when creating or editing assets. Hidden fields
          will not appear in the asset form, and required fields must be filled in.
        </Text>
      </Alert>

      <Paper withBorder>
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Field Name</Table.Th>
              <Table.Th>Visible</Table.Th>
              <Table.Th>Required</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {(Object.entries(fieldSettings) as [keyof AssetFieldSettingsType, AssetFieldConfig][]).map(
              ([fieldName, config]) => (
                <Table.Tr key={fieldName}>
                  <Table.Td>
                    <Text fw={500}>{config.label || fieldName}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Switch
                      checked={config.visible}
                      onChange={(e) => handleVisibilityChange(fieldName, e.currentTarget.checked)}
                    />
                  </Table.Td>
                  <Table.Td>
                    <Switch
                      checked={config.required}
                      onChange={(e) => handleRequiredChange(fieldName, e.currentTarget.checked)}
                      disabled={!config.visible}
                    />
                  </Table.Td>
                </Table.Tr>
              )
            )}
          </Table.Tbody>
        </Table>
      </Paper>

      {hasChanges && (
        <Group justify="flex-end">
          <Button variant="subtle" onClick={handleReset}>
            Reset
          </Button>
          <Button
            leftSection={<IconDeviceFloppy size={16} />}
            onClick={handleSave}
            loading={updateSettings.isPending}
          >
            Save Changes
          </Button>
        </Group>
      )}
    </Stack>
  );
}
